import DateOptions from "app/components/AdaptiveDatePicker/DateOptions";
import DatePill from "app/components/AdaptiveDatePicker/DatePill";
import { Calendar } from "app/components/Calendar";
import { format } from "date-fns";
import React, { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { DateRangeFilterValue } from "../../types";

interface FilterDateRangePickerProps {
  value: DateRangeFilterValue;
  onChange: (value: DateRangeFilterValue) => void;
}

const formatDisplayDate = (dateValue: string | null) => {
  if (!dateValue) return "";
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return "";
  return format(date, "dd MMM, yyyy");
};

const toStoredRange = (range: DateRange): DateRangeFilterValue => ({
  startDate: range.from ? format(range.from, "yyyy-MM-dd") : null,
  endDate: range.to ? format(range.to, "yyyy-MM-dd") : null,
});

export default function FilterDateRangePicker({
  value,
  onChange,
}: FilterDateRangePickerProps) {
  const dateRange = useMemo<DateRange>(
    () => ({
      from: value.startDate ? new Date(value.startDate) : undefined,
      to: value.endDate ? new Date(value.endDate) : undefined,
    }),
    [value.endDate, value.startDate],
  );

  const updateRange = (range: DateRange) => {
    onChange(toStoredRange(range));
  };

  const handlePresetSelect = (date: Date) => {
    updateRange({ from: date, to: date });
  };

  return (
    <div
      className="w-full"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <div className="flex items-stretch border-b border-[#E9ECF2]">
        <div className="flex-1 min-w-0 border-r border-[#E9ECF2]">
          <DatePill
            date={formatDisplayDate(value.startDate)}
            onClear={() => updateRange({ ...dateRange, from: undefined })}
            iconShow={Boolean(value.startDate)}
            placeHolder="From date"
            className="!p-3"
            textClassName="!text-[11px] text-secondary capitalize"
          />
        </div>
        <div className="flex-1 min-w-0">
          <DatePill
            date={formatDisplayDate(value.endDate)}
            onClear={() => updateRange({ ...dateRange, to: undefined })}
            iconShow={Boolean(value.endDate)}
            placeHolder="To date"
            className="!p-3"
            textClassName="!text-[11px] text-secondary capitalize"
          />
        </div>
      </div>

      <div className="flex items-stretch min-h-[280px]">
        <DateOptions
          onSelectDate={handlePresetSelect}
          className="!w-[148px] !min-w-[148px] !bg-[#F9FAFB] !border-0 !border-r !border-[#E9ECF2] shrink-0"
        />
        <div className="flex-1 p-3">
          <Calendar
            mode="range"
            selected={dateRange}
            onSelect={(range) => {
              updateRange(range ?? { from: undefined, to: undefined });
            }}
            required={false}
            showOutsideDays
            className="w-full"
            monthGridClassName="w-full"
          />
        </div>
      </div>
    </div>
  );
}
